/**
 * demoClock.ts — Dataset-Anchored Demo Time
 *
 * The demo anchor is set to the 20th of the current month.
 * This ensures ~20 days of data within the same month so the
 * month view calendar, body stats trends, and comparison charts
 * all look populated. 14 days of nutrition + workout history
 * fits entirely within the current month.
 *
 * All demo data is generated relative to this anchor using daysAgo offsets,
 * so changing the anchor automatically shifts every entry forward.
 *
 * Place at: src/demo/demoClock.ts
 */

import dayjs from 'dayjs';

/**
 * The demo anchor — 20th of the current real month.
 * Computed once at module load so it's stable within a session.
 */
const DEMO_ANCHOR = dayjs().date(20).format('YYYY-MM-DD');

/**
 * Called by demoDetection.ts at boot. No-op now — anchor is derived at load.
 * Kept for API compatibility so demoDetection doesn't need changes.
 */
export function anchorDemoClock(): void {
  // No-op. Anchor is computed at module load.
}

/**
 * Returns the demo "today" — the anchor date all entries are seeded against.
 * Deterministic within a session.
 */
export function demoToday(): string {
  return DEMO_ANCHOR;
}

/**
 * Returns the anchor as a Date object (noon local time to avoid DST edge cases).
 */
export function demoNow(): Date {
  return new Date(DEMO_ANCHOR + 'T12:00:00');
}

/**
 * Returns "yesterday" relative to the anchor.
 */
export function demoYesterday(): string {
  const d = new Date(DEMO_ANCHOR + 'T12:00:00');
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
